import { createContext, useContext } from 'react';
import type { JSX, ReactNode } from 'react';
/* LE GLYPHE DU FONDATEUR, pas un Streamline : la chaine de `link.svg`
   (`icons/custom/`). Il n'entre ici, et pas dans les vues, que pour une raison
   de GRAPHE : `TableView` -> `LinkedCard` -> `views/core/index` -> `TableView`
   fermerait un cycle si la marque vivait dans la carte liee. Ce fichier, lui,
   ne depend que de react, du contrat et de l'assembleur. */
import { IconLink } from '../icons/custom/index.js';
import { estLienParListe } from '../views/contract.js';
import type {
  ChoixDeReferenceDef,
  FieldMap,
  FieldRole,
  LienParLaListe,
  LienParLeChampLie,
  LinkedCardDef,
  LinkedDoor,
  ProjectedRow,
  RollupFieldDef,
  SortLevel,
  ViewBinding,
  ViewColumn,
} from '../views/contract.js';
/* LE COMPARATEUR DE L'ECRAN, ET PAS UN AUTRE : les deux surfaces de lien qui
   trient (la carte, la fenetre de choix) rangent leurs lignes exactement comme
   une liste ordinaire. Ce module ne depend que de react, du contrat et de
   l'assembleur ; `views/project.ts` n'importe rien d'ici, donc l'ajout ne ferme
   aucun cycle. */
import { compareCells, matchesSearch, sortValueOf } from '../views/project.js';
/* LE NOM DU SAC SUR-MESURE, lu de l'assembleur et jamais reecrit : `customFields`
   ecrit ici en clair serait le meme fait a deux endroits, et le jour ou l'un
   des deux bouge, une valeur imposee se rangerait a cote du champ qu'elle vise. */
import { SAC_SUR_MESURE } from '../views/screens.js';

/**
 * LES LIENS ENTRE PORTES, cote SOCLE : le PORT, et les regles PURES qui vont
 * avec.
 *
 * CE QUE CE FICHIER NE FAIT PAS, ET POURQUOI. Il n'importe pas `@taki/sdk` et
 * il n'en importera jamais : l'ordre de build est sdk -> ui -> shell -> apps,
 * et le socle d'interface ne peut pas dependre de ce qui est construit apres
 * lui. C'est la meme frontiere que `lib/templates.tsx`, et le meme partage des
 * roles : le socle decrit la FORME du service (quatre verbes de LECTURE),
 * l'HOTE la remplit avec `sdk.repositoryOf(family)`, et la carte liee ne
 * connait que cette forme.
 *
 * CE QUE LE PORT SERT, ET RIEN D'AUTRE : « les interventions de CE materiel ».
 * Une porte declare ses LIENS (`liens: [{ id, porte, par }]`), une carte de
 * fiche en NOMME un, et le socle en tire une requete cadree par la fiche
 * ouverte. Le filtre se fait donc au SERVEUR (une egalite `where`), jamais en
 * memoire : charger une collection entiere pour la filtrer ensuite est
 * exactement le fork que le chantier refuse.
 *
 * LECTURE SEULE (PR 1), A UNE EXCEPTION QUI SE NOMME : `rattacher`. Ni
 * creation, ni archivage, et toujours aucune ecriture sur la fiche OUVERTE :
 * suivre un lien, c'est afficher, compter et totaliser. Le seul geste de la
 * carte qui ECRIT est « Lier une fiche existante », et il ecrit sur l'ENFANT
 * choisi, une seule clef, le champ de reference du lien (`link.by`). Ce n'est
 * pas une entorse a la promesse, c'est le SENS du lien : la reference vit dans
 * le document de celui qui DESIGNE, jamais dans celui qui est designe, donc
 * rattacher ne peut s'ecrire nulle part ailleurs. Cote SDK, `FamilyRepository`
 * promet `update` a cote des quatre lectures, et c'est tout ce que l'hote
 * appelle : une ligne, aucune connaissance de la famille.
 *
 * LE PORT EST FACULTATIF, comme celui des modeles : `useLiensPort()` rend
 * `null` quand personne ne le fournit (un banc d'essai, un test, un shell sans SDK),
 * et la carte liee rend alors un bloc GRISE avec sa raison (G16), jamais une
 * erreur. Une app montee sans hote n'est pas cassee, elle est incomplete, et
 * ca se lit.
 */

/**
 * LE CADRAGE D'UN LIEN : ce qui designe les fiches liees, et rien de plus.
 *
 * C'est ce que les TROIS agregats prennent, exactement comme `CountOptions`
 * cote SDK : un agregat prend le cadrage d'une liste, jamais sa fenetre. Un
 * total qui ne compterait que la page affichee mentirait des la premiere fiche
 * hors plafond (AGENTS.md section 7).
 */
export interface CadrageDeLien {
  /** Le sous-type du jeu lie, quand la porte liee en declare un. */
  type?: string;
  /**
   * Les egalites du lien : `{ [lien.by]: id de la fiche ouverte }`. VIDE pour un
   * lien d'appartenance (`contient`) : il n'a pas d'egalite.
   */
  where: Record<string, unknown>;
  /**
   * L'APPARTENANCE (lien `par` dont le champ du jeu lie est une LISTE d'ids) :
   * les fiches dont la liste `champ` CONTIENT `valeur`, l'id de la fiche
   * ouverte. L'hote le relaie tel quel en `arrayContains` du SDK, sur la
   * lecture comme sur les trois agregats. `champ` est un chemin de stockage.
   */
  contient?: { champ: string; valeur: string };
  /**
   * LES ARCHIVES DANS UN AGREGAT : absent (le defaut, les fiches ACTIVES seules,
   * inchange) ou `'all'`.
   *
   * QUI DEMANDE `'all'`, ET POURQUOI LUI SEUL. Le compte d'un rollup et le pied
   * d'un tableau parlent de ce que la carte MONTRE, c'est-a-dire des fiches
   * vivantes : ils ne demandent rien. Le REFUS DE SUPPRESSION, lui, compte ce
   * qui RESTERAIT ACCROCHE a une fiche effacee, et une intervention archivee y
   * est toujours accrochee : archiver n'est pas detacher. C'est le seul appelant
   * de cette clef (`comptesLies`, plus bas).
   *
   * `'only'` N'EXISTE PAS ICI, comme dans `ArgumentsDeListe` : personne n'a de
   * raison d'agreger UNIQUEMENT des archives, et une valeur qu'aucun appelant
   * n'emploie est une branche que rien ne verifie.
   *
   * LES SUPPRIMES NE SONT DANS AUCUN DES DEUX : depuis le 09/09/2026, le SDK
   * pose `deletedAt == null` au SERVEUR sur tout agregat hors `'exclude'`
   * (`aggregateFilters`, `packages/sdk/src/list-query.ts`). Sans ca, `'all'`
   * aurait compte la corbeille, et le refus aurait bloque une suppression que
   * plus rien ne justifie.
   */
  archived?: 'all';
}

/**
 * Le cadrage, plus la FENETRE : ce que la carte liee charge pour l'afficher.
 *
 * AUCUN TRI SERVEUR, ET C'EST UNE DECISION. Firestore EXCLUT d'une requete les
 * documents ou le champ d'un `orderBy` est absent. Sur un champ FACULTATIF (la
 * plupart des champs sur-mesure), `list` et `count` cesseraient alors de couvrir
 * le meme jeu : le compte dirait 3, la carte chargerait 2, elle s'annoncerait
 * « partielle » et afficherait « 2 / 3 » sans qu'aucune ligne ne manque
 * vraiment. Une carte VIDE a cote d'un compte a 3 est le meme defaut, en pire :
 * rien ne le signale.
 *
 * LE TRI EST DONC CLIENT, sur la fenetre chargee (au plus `limit`, donc au plus
 * 500 lignes pour une carte, le plafond de `PLAFOND_CARTE_LIEE_MAX`) : la pile
 * de l'ecran d'abord, puis les gestes de l'en-tete. Le
 * prix est connu et il est ecrit : au-dela du plafond, la fenetre est celle que
 * le serveur rend en ORDRE NATUREL, pas « les 200 plus recentes ».
 */
export interface ArgumentsDeListe {
  /** Le sous-type du jeu, quand la porte en declare un. */
  type?: string;
  /**
   * LES EGALITES, OU RIEN DU TOUT.
   *
   * Une CARTE LIEE cadre toujours (`{ [lien.by]: id de la fiche ouverte }`) :
   * charger un jeu entier pour le filtrer en memoire est exactement le fork que
   * le chantier refuse.
   *
   * UN ANNUAIRE, LUI, CHARGE TOUT UN JEU, et c'est son travail : il resout les
   * NOMS de toutes les fiches qu'une colonne de reference peut designer, donc
   * il n'a rien a cadrer d'autre que le sous-type. Une egalite y retiendrait
   * justement les fiches deja liees, c'est-a-dire l'inverse de ce qu'un choix
   * demande. C'est la seule raison pour laquelle `where` est facultatif ICI et
   * obligatoire sur les trois agregats : un total sans cadrage compterait le
   * jeu entier et rendrait un nombre plausible.
   */
  where?: Record<string, unknown>;
  /** L'appartenance du lien (voir `CadrageDeLien.contient`), absente pour une egalite. */
  contient?: { champ: string; valeur: string };
  /**
   * LES ARCHIVES : `'exclude'` (le defaut, quand la clef est absente) ou
   * `'all'`. MEME VOCABULAIRE QUE LE SDK (`ListOptions.archived`), et c'est
   * voulu : l'hote relaie la valeur telle quelle, sans table de traduction qui
   * inverserait un jour le sens d'un mot.
   *
   * QUI DEMANDE QUOI, ET POURQUOI. Une CARTE LIEE montre les fiches VIVANTES
   * d'une fiche parente : elle ne demande rien, donc `'exclude'`. Un ANNUAIRE
   * demande `'all'` : il RESOUT DES NOMS, et une reference peut pointer une
   * fiche archivee. Sans les archives, cette cellule dirait « Fiche liee
   * introuvable » sur une fiche qui existe toujours, ce qui envoie chercher une
   * suppression qui n'a pas eu lieu.
   *
   * `'only'` N'EXISTE PAS ICI, et ce n'est pas un oubli : personne n'a de
   * raison de resoudre UNIQUEMENT des archives, et une valeur qu'aucun appelant
   * n'emploie est une branche que rien ne verifie.
   */
  archived?: 'exclude' | 'all';
  /**
   * Le plafond de la lecture : ce qu'elle charge, jamais ce qui existe. Pour une
   * carte, sa fenetre ; pour un annuaire, la taille d'UNE page.
   */
  limit: number;
  /**
   * LES FICHES DONT ON CONNAIT LES IDS (trente au plus), lues en UNE requete :
   * c'est la lecture PAR LOT d'un annuaire qui nomme des fiches hors de sa
   * fenetre (`souhaiterTitres`, `lib/annuaires.tsx`). Meme vocabulaire que le
   * SDK (`ListOptions.ids`), relaye tel quel par l'hote. Une liste cadree par
   * l'org et non un `get` : un id absent y rend moins de lignes, pas une erreur
   * de droits. Jamais avec `where`.
   */
  ids?: readonly string[];
  /**
   * LE CURSEUR D'UNE PAGE SUIVANTE : l'id du dernier document de la page
   * precedente, comme `ListOptions.after` du SDK. Seul l'annuaire s'en sert, pour
   * lire un jeu au-dela d'une page (`listerParPages`, `lib/annuaires.tsx`).
   */
  after?: string;
}

/**
 * CE QUE L'HOTE FOURNIT : quatre verbes de LECTURE sur une famille designee par
 * son NOM, plus UN verbe d'ecriture, `rattacher`.
 *
 * `family` EST UNE CHAINE, et c'est le fond du chantier : la famille du jeu lie
 * (`assets`, `careRecords`, au PLURIEL, comme `jeu.family`) n'est connue qu'a
 * l'execution. L'hote la resout par `sdk.repositoryOf(family)`, qui leve sur
 * une famille inconnue plutot que de rendre `undefined`.
 */
export interface LiensPort {
  /**
   * La fenetre de fiches : deja cadree et plafonnee.
   *
   * DEUX APPELANTS, DEUX CADRAGES : une CARTE LIEE demande les fiches d'UNE
   * fiche parente (`where`), un ANNUAIRE demande TOUT un jeu pour en resoudre
   * les noms (pas de `where`). Un seul verbe pour les deux : c'est la meme
   * lecture, et l'hote la sert avec le meme `sdk.repositoryOf(family).list`.
   */
  list(family: string, args: ArgumentsDeListe): Promise<Array<Record<string, unknown>>>;
  /**
   * Le total SERVEUR : le « 312 » de « 200 / 312 », le compte d'un rollup, et
   * le compte du REFUS DE SUPPRESSION (`comptesLies`, seul appelant a demander
   * `archived: 'all'`).
   */
  count(family: string, args: CadrageDeLien): Promise<number>;
  /** La somme d'un champ du jeu lie. Rend 0 quand aucune fiche ne repond. */
  sum(family: string, field: string, args: CadrageDeLien): Promise<number>;
  /**
   * LES FICHES D'UNE FAMILLE, LUES PAR LEURS IDS (la pondération d'un total :
   * `RollupFieldDef.times`). Rend celles qui existent ; un id inconnu est
   * ABSENT du resultat, ce n'est pas une erreur.
   *
   * C'EST L'HOTE QUI DECIDE COMMENT, et le port ne promet que la forme : une
   * lecture par id DISTINCT, jamais une par ligne qui le cite, cadree par l'org
   * par le chemin du document. Les ids sont deja dedoublonnes par l'appelant.
   */
  lireParIds(family: string, ids: readonly string[]): Promise<Array<Record<string, unknown>>>;
  /**
   * La moyenne d'un champ du jeu lie. Rend `null` sans aucune fiche, et ce
   * `null` NE SE TRANSFORME PAS EN ZERO : « aucune fiche » et « moyenne nulle »
   * sont deux faits differents, et le second est un chiffre qu'on croirait.
   */
  average(family: string, field: string, args: CadrageDeLien): Promise<number | null>;
  /**
   * RATTACHE UNE FICHE QUI EXISTE DEJA : la fiche `id` de `family` recoit
   * `valeur` (l'id de la fiche ouverte) a son `chemin` (le `by` du lien).
   *
   * C'EST L'ENFANT QU'ON ECRIT, ET LUI SEUL. Une reference vit dans le document
   * de celui qui DESIGNE : rattacher une personne a une societe, c'est poser
   * l'id de la societe SUR la personne. La fiche ouverte, elle, ne bouge pas
   * d'un octet, et c'est pour ca que ce verbe ne porte pas son id ailleurs que
   * dans `valeur`.
   *
   * UNE REFERENCE EST A VALEUR UNIQUE : rattacher une fiche deja liee ailleurs
   * la DEPLACE, elle ne l'ajoute pas. C'est la SURFACE qui doit le dire avant le
   * clic (`candidatsARattacher`, mention « Deja liee ailleurs ») ; ce verbe-ci
   * ecrit ce qu'on lui demande, sans deviner.
   *
   * `chemin` EST UN CHEMIN DE STOCKAGE (`productId`,
   * `customFields.fournisseurId`), tel que l'assemblage l'a pose : l'hote le
   * range par `patchDeRattachement`, jamais a la main. Un champ sur-mesure pose
   * a la RACINE passe la validation et rend la fiche invisible de TOUTES les
   * cartes liees, sans la moindre erreur.
   *
   * IL NE REND RIEN : la carte ne relit pas la fiche ecrite, elle RECHARGE sa
   * liste et son compte au serveur. Rendre le document mis a jour aurait fait
   * croire qu'on peut s'en contenter, alors que le compte, lui, se demande.
   */
  rattacher(family: string, id: string, chemin: string, valeur: string): Promise<void>;
  /**
   * AJOUTE `valeur` A LA LISTE de la fiche `id` de `family`, au `chemin` de
   * stockage de son champ liste. L'hote LIT la fiche (`get`), calcule la liste
   * (`listeApres`, sans doublon : ajouter deux fois laisse un seul id) et
   * ECRIT le patch range par le socle (`patchDeRattachement`) : le sac
   * `customFields` se fusionne clef par clef cote SDK.
   *
   * LA COURSE ENTRE DEUX ECRITURES EST ASSUMEE (lecture puis ecriture, sans
   * transaction), comme pour `link_records` du MCP : deux personnes qui
   * ajoutent au meme instant a la meme liste peuvent s'ecraser.
   *
   * Sert deux gestes : « Parcourir » d'une carte d'APPARTENANCE (l'enfant gagne
   * l'id de la fiche ouverte) et la fiche creee depuis une carte de LISTE (la
   * fiche ouverte gagne l'id cree). Il ne rend rien : la carte recharge.
   */
  ajouterALaListe(family: string, id: string, chemin: string, valeur: string): Promise<void>;
  /**
   * RETIRE `valeur` DE LA LISTE de la fiche `id` : le geste « Retirer de la
   * liste » d'une carte d'APPARTENANCE (chaque enfant choisi perd l'id de la
   * fiche ouverte). Meme forme que `ajouterALaListe`. Retirer un id absent n'ecrit
   * rien.
   */
  retirerDeLaListe(family: string, id: string, chemin: string, valeur: string): Promise<void>;
}

/**
 * LE CONTEXTE, DEFAUT `null`, et ce defaut est LU : un port absent est un fait
 * ordinaire, il grise la carte avec sa raison, il ne casse rien.
 */
const LiensContext = createContext<LiensPort | null>(null);

/**
 * Fournisseur pose par l'HOTE (celui qui a le SDK), autour du shell. Meme
 * patron que `ModelesProvider` : le socle decrit, l'exterieur remplit.
 */
export function LiensProvider({
  value,
  children,
}: {
  value: LiensPort | null;
  children: ReactNode;
}): JSX.Element {
  return <LiensContext.Provider value={value}>{children}</LiensContext.Provider>;
}

/** Le port des liens, ou `null` quand l'hote n'en fournit pas. */
export function useLiensPort(): LiensPort | null {
  return useContext(LiensContext);
}

/**
 * LA RAISON QUI ETEINT UNE CARTE LIEE, ECRITE ICI ET NULLE PART AILLEURS.
 *
 * Une seule, contrairement aux modeles qui en ont trois : les deux autres
 * manques possibles (le lien, la porte liee) sont refuses A L'ASSEMBLAGE, avec
 * leur nom (`views/screens.ts`). Ce qui reste est le seul manque qu'aucune
 * declaration ne peut couvrir : l'hote n'a pas monte de `LiensProvider`.
 */
export const RAISON_LIENS = {
  /** Personne n'a monte de `LiensProvider` au-dessus de l'ecran. */
  sansPort: "L'hote ne fournit pas les fiches liees.",
} as const;

/* ------------------------------------------------------------------ */
/* Le signe du lien : la marque posee a cote d'un libelle              */
/* ------------------------------------------------------------------ */

/**
 * LES DEUX MOTS DE LA MARQUE, ECRITS ICI ET NULLE PART AILLEURS.
 *
 * Ils ne sont pas interchangeables, et le NOMBRE est tout ce qui les separe :
 * une COLONNE de reference porte l'id d'UNE fiche d'ailleurs (« Fiche liee »),
 * le titre d'une CARTE liee annonce TOUTES les fiches d'ailleurs qui pointent
 * la fiche ouverte (« Fiches liees »). C'est le meme lien, lu dans ses deux
 * sens, et une infobulle qui dirait le meme mot des deux cotes effacerait
 * justement ce que la marque apprend.
 *
 * DES LIBELLES, PAS DES PHRASES (G26) : un nom nu, sans article ni verbe.
 */
export const LIBELLE_LIEN = {
  /** Le libelle d'une colonne de reference : la case designe UNE fiche. */
  fiche: 'Fiche liee',
  /** Le titre d'une carte liee : la carte montre LES fiches qui la designent. */
  carte: 'Fiches liees',
} as const;

/**
 * LA MARQUE DE LIEN : le petit signe qui dit « ceci n'est pas une valeur, c'est
 * une autre fiche ».
 *
 * POURQUOI ELLE EXISTE. Une colonne de reference ressemble a n'importe quelle
 * autre colonne : elle affiche un mot (« Sony FX9 »), et rien ne distingue ce
 * mot d'un texte saisi a la main. Le lecteur ne peut donc pas savoir qu'il y a
 * une fiche derriere, ni pourquoi ce champ s'edite par une liste plutot qu'au
 * clavier. La marque est ce que le socle a de moins cher pour le dire, et elle
 * le dit AU MEME ENDROIT dans les trois surfaces qui portent un lien : le
 * libelle d'un champ de fiche, l'en-tete d'une colonne, le titre d'une carte
 * liee.
 *
 * ELLE EST DE NATURE, PAS D'ETAT : elle ne change ni au survol, ni au tri, ni
 * quand la case s'ouvre. Son encre est donc toujours `ink-400`, l'encre des
 * libelles, jamais celle du texte actif : un signe qui s'allume se lit comme un
 * controle, et celui-ci ne se clique pas.
 *
 * ELLE NE PARLE PAS AUX LECTEURS D'ECRAN (`aria-hidden`), et c'est deliberent :
 * le nom accessible d'un en-tete de colonne est deja fixe (`aria-label`), et
 * une annonce « lien » sur chaque cellule ferait entendre un lien HTML la ou il
 * n'y en a aucun. Ce qu'elle porte est une INFOBULLE, pour l'oeil qui la
 * decouvre.
 *
 * `size` EST TOUJOURS DONNE PAR L'APPELANT : la marque se cale sur le texte
 * qu'elle accompagne (un libelle de champ, un en-tete en capitales, un titre de
 * carte n'ont pas la meme hauteur), et un defaut unique aurait fait grossir la
 * marque la ou le texte, lui, retrecit.
 */
export function MarqueDeLien({ titre, size }: { titre: string; size: number }): JSX.Element {
  return (
    <span title={titre} aria-hidden className="inline-flex shrink-0 text-ink-400">
      <IconLink size={size} />
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Le sac, relu DEFENSIVEMENT                                          */
/* ------------------------------------------------------------------ */

/*
 * POURQUOI ON RELIT CE QUE LE SOCLE VIENT D'ASSEMBLER. Les cartes voyagent dans
 * le sac libre du binding (`ViewBinding.options`), qui est un
 * `Record<string, unknown>` : rien n'y est garanti par le type. Il vient
 * souvent de `bindingsDe` (assemble, donc juste), mais une app a parfaitement
 * le droit d'ecrire ses cartes a la main, et c'est le contrat. Une lecture
 * naive planterait la fiche ENTIERE sur une declaration approximative.
 *
 * ON SAUTE, ET ON LE DIT. Une entree invalide est ignoree (la fiche vit), mais
 * en developpement elle laisse une trace nommee : une carte qui disparait sans
 * un mot est exactement le genre de panne qu'on cherche pendant une heure.
 */

/** Un lien resolu, relu du sac : ses quatre noms, ou rien. */
function lienDuSac(valeur: unknown): LinkedDoor | null {
  if (typeof valeur !== 'object' || valeur === null) return null;
  const brut = valeur as Record<string, unknown>;
  const nom = (clef: string): string | null => {
    const lu = brut[clef];
    return typeof lu === 'string' && lu !== '' ? lu : null;
  };
  const id = nom('id');
  const door = nom('door');
  const by = nom('by');
  const liste = nom('liste');
  const family = nom('family');
  /* EXACTEMENT UN DES DEUX CHEMINS : `by` (le champ du jeu lie) ou `liste` (le
     champ liste de la fiche ouverte). Les deux, ou aucun, ne disent pas ou lire. */
  if (id === null || door === null || family === null || (by === null) === (liste === null)) return null;
  const type = nom('type');
  const commun = { id, door, family, ...(type === null ? {} : { type }) };
  if (liste !== null) return { ...commun, liste };
  return { ...commun, by: by as string, ...(brut['multiple'] === true ? { multiple: true as const } : {}) };
}

/** Les trois agregats du contrat, et rien d'autre. */
function agregatDuSac(valeur: unknown): 'count' | 'sum' | 'avg' | null {
  return valeur === 'count' || valeur === 'sum' || valeur === 'avg' ? valeur : null;
}

/**
 * UNE CARTE LIEE, relue du sac : l'objet TEL QUEL quand il tient debout.
 *
 * TEL QUEL, ET C'EST IMPORTANT : la carte garde son IDENTITE d'un rendu a
 * l'autre (le sac du binding, lui, est memoise par le moteur), et c'est cette
 * identite qui evite a la carte de recharger ses fiches liees a chaque frappe.
 */
export function carteLieeDuSac(valeur: unknown): LinkedCardDef | null {
  if (typeof valeur !== 'object' || valeur === null) return null;
  const brut = valeur as Record<string, unknown>;
  const titre = brut['title'];
  const vue = brut['view'];
  const plafond = brut['limit'];
  const binding = brut['binding'];
  const lien = lienDuSac(brut['link']);
  const debout =
    typeof titre === 'string' &&
    titre.trim() !== '' &&
    (vue === 'core.liste' || vue === 'core.tableau') &&
    typeof plafond === 'number' &&
    Number.isInteger(plafond) &&
    plafond > 0 &&
    typeof binding === 'object' &&
    binding !== null &&
    Array.isArray(brut['sort']) &&
    Array.isArray(brut['totals']) &&
    lien !== null;
  if (!debout) {
    /* `import.meta.env?.DEV` et non `import.meta.env.DEV` : ce module est le
       seul du chantier a tourner AUSSI hors navigateur (les tests `node --test`
       l'importent pour ses fonctions pures), et la, `import.meta.env` n'existe
       pas du tout. Sans le `?.`, la lecture leverait avant la garde. */
    if (import.meta.env?.DEV === true) {
      console.warn(
        `[taki/liens] Carte liee ignoree (${typeof titre === 'string' ? titre : 'sans titre'}) : ` +
          'il lui manque un titre, un lien resolu, une vue montable, un plafond, un binding, ' +
          'un tri ou ses totaux.',
      );
    }
    return null;
  }
  return valeur as LinkedCardDef;
}

/**
 * LA CONDITION DES CHOIX DES CHAMPS DE LIEN, relue du sac (`options.choixPermis`) :
 * une fonction par clef de colonne (`ColumnReference.choixPermis`). Une entree
 * qui n'est pas une fonction est ecartee seule : le champ propose alors tout son
 * jeu, comme sans condition.
 */
export function choixPermisDuSac(
  valeur: unknown,
): Record<string, (fiche: Record<string, unknown>) => boolean> {
  if (typeof valeur !== 'object' || valeur === null || Array.isArray(valeur)) return {};
  const retenus: Record<string, (fiche: Record<string, unknown>) => boolean> = {};
  for (const [clef, regle] of Object.entries(valeur as Record<string, unknown>)) {
    if (typeof regle === 'function') retenus[clef] = regle as (fiche: Record<string, unknown>) => boolean;
  }
  return retenus;
}

/**
 * LES GESTES DES CHAMPS DE LIEN D'UNE FICHE, relus du sac
 * (`options.choixDeReference`) : une entree par clef de colonne, chacune TELLE
 * QUELLE quand elle tient debout.
 *
 * TELLE QUELLE, pour la meme raison que la carte (`carteLieeDuSac`) : son
 * identite est stable d'un rendu a l'autre, et la fenetre de choix ne relit
 * pas ses candidates a chaque frappe. Une entree bancale est ECARTEE seule : le
 * champ garde sa liste, sans les deux gestes, plutot que de faire tomber la
 * fiche.
 */
export function choixDeReferenceDuSac(valeur: unknown): Record<string, ChoixDeReferenceDef> {
  if (typeof valeur !== 'object' || valeur === null || Array.isArray(valeur)) return {};
  const retenus: Record<string, ChoixDeReferenceDef> = {};
  for (const [clef, brut] of Object.entries(valeur as Record<string, unknown>)) {
    const entree = typeof brut === 'object' && brut !== null ? (brut as Record<string, unknown>) : null;
    const retour = entree?.['retour'];
    const binding = entree?.['bindingChoix'];
    const debout =
      entree !== null &&
      typeof entree['family'] === 'string' &&
      entree['family'] !== '' &&
      typeof binding === 'object' &&
      binding !== null &&
      typeof retour === 'object' &&
      retour !== null &&
      typeof (retour as Record<string, unknown>)['family'] === 'string' &&
      typeof (retour as Record<string, unknown>)['chemin'] === 'string';
    if (debout) {
      retenus[clef] = brut as ChoixDeReferenceDef;
      continue;
    }
    if (import.meta.env?.DEV === true) {
      console.warn(
        `[taki/liens] Gestes du champ de lien "${clef}" ignores : il leur manque un jeu vise, ` +
          'un binding de choix ou le retour (jeu de la fiche, chemin du champ).',
      );
    }
  }
  return retenus;
}

/**
 * UN CHAMP DE TOTAL, relu du sac : `{ rollup: { label, link, agg, field? } }`,
 * ou rien. C'est ce qui distingue, dans les clefs d'une carte de champs, un
 * total d'un nom de champ nu.
 */
export function totalDuSac(valeur: unknown): RollupFieldDef | null {
  if (typeof valeur !== 'object' || valeur === null) return null;
  const enveloppe = (valeur as Record<string, unknown>)['rollup'];
  if (typeof enveloppe !== 'object' || enveloppe === null) return null;
  const brut = enveloppe as Record<string, unknown>;
  const label = brut['label'];
  const agg = agregatDuSac(brut['agg']);
  const lien = lienDuSac(brut['link']);
  if (typeof label !== 'string' || label.trim() === '' || agg === null || lien === null) {
    return null;
  }
  const champ = brut['field'];
  /* `count` NE PORTE AUCUN CHAMP (il compte des fiches), les deux autres en
     exigent un : sans lui, il n'y a rien a sommer. Le schema le refuse deja au
     poste de developpement ; ici on protege le rendu. */
  if (agg !== 'count' && (typeof champ !== 'string' || champ === '')) return null;
  /* L'HABILLAGE PASSE, quand l'assemblage en a pose un. Ce relecteur REBATIT
     l'objet clef par clef (c'est ce qui le rend sur) : une clef oubliee ici est
     une clef perdue en silence, et c'est ce qui affichait « 1890 » sous un
     total en euros. */
  const format = brut['format'];
  const times = ponderationDuSac(brut['times'], agg);
  return {
    label,
    link: lien,
    agg,
    ...(typeof format === 'function' ? { format: format as (valeur: number) => string } : {}),
    ...(agg === 'count' || typeof champ !== 'string' ? {} : { field: champ }),
    ...(times === null ? {} : { times }),
  };
}

/**
 * LA PONDERATION D'UN TOTAL, relue du sac : les trois chemins et la famille
 * designee, ou rien. Elle ne vaut que pour une SOMME (le schema le refuse
 * ailleurs) ; une pondération a moitie ecrite est ecartee ici plutot que de
 * produire un total faux.
 */
function ponderationDuSac(valeur: unknown, agg: 'count' | 'sum' | 'avg'): RollupFieldDef['times'] | null {
  if (agg !== 'sum' || typeof valeur !== 'object' || valeur === null) return null;
  const brut = valeur as Record<string, unknown>;
  const { by, family, type, field } = brut;
  if (typeof by !== 'string' || by === '' || typeof family !== 'string' || family === '') return null;
  if (typeof field !== 'string' || field === '') return null;
  return { by, family, field, ...(typeof type === 'string' && type !== '' ? { type } : {}) };
}

/* ------------------------------------------------------------------ */
/* Ce qu'on demande au port : des arguments, pas une requete           */
/* ------------------------------------------------------------------ */

/**
 * LE CADRAGE D'UN LIEN, POSE UNE SEULE FOIS. La carte, le pied et les champs de
 * total le demandent tous les trois, et trois copies de `{ [by]: id }`
 * divergeraient le jour ou le lien porterait autre chose qu'une egalite.
 */
export function cadrageDeLien(lien: LinkedDoor, id: string): CadrageDeLien {
  if (estLienParListe(lien)) {
    /* LE SENS DIRECT N'A PAS DE CADRAGE SERVEUR : les ids se lisent sur la
       fiche ouverte, puis les fiches par leurs ids (`lireLaListe`). Arriver ici
       est une erreur d'appelant, pas une donnee : on la leve plutot que de
       cadrer sur un champ qui n'existe pas dans le jeu lie. */
    throw new Error(
      `[taki/liens] Le lien "${lien.id}" se declare par "liste" : il n'a pas de cadrage serveur ` +
        '(utiliser `lireLaListe`).',
    );
  }
  return {
    ...(lien.type === undefined ? {} : { type: lien.type }),
    /* `by` EST DEJA UN CHEMIN DE STOCKAGE (`assetId`, `customFields.materielId`),
       pose par l'assemblage : on le prend TEL QUEL. Le rederiver ici en ferait
       la meme regle a deux endroits, et l'egalite d'un champ sur-mesure ne
       retiendrait rien, sans la moindre erreur.

       UN LIEN D'APPARTENANCE (`multiple`) CADRE PAR `contient` : le champ du jeu
       lie est une liste, et une egalite sur une liste ne retient RIEN (sans
       erreur non plus). */
    ...(lien.multiple === true
      ? { where: {}, contient: { champ: lien.by, valeur: id } }
      : { where: { [lien.by]: id } }),
  };
}

/**
 * LES ARGUMENTS DE `list` POUR UNE CARTE LIEE : le cadrage du lien, et le
 * plafond de la carte. RIEN D'AUTRE, et surtout aucun `orderBy`.
 *
 * UNE EGALITE SEULE N'EXCLUT PERSONNE, un `orderBy` si : Firestore laisse de
 * cote les documents ou le champ trie est absent. La carte demanderait alors sa
 * fenetre a un jeu et son compte a un autre, et l'ecart se lirait comme un
 * plafond atteint (« 2 / 3 »), voire comme « Aucune fiche liee » a cote d'un
 * compte a 3. Aucune carte liee ne reclame donc d'index composite : c'est le
 * corollaire agreable de la meme decision.
 *
 * FONCTION PURE ET EXPORTEE EXPRES : c'est la seule chose de la carte qui
 * puisse se tromper en SILENCE (une egalite posee sur le mauvais champ rend une
 * carte vide, jamais une erreur), et un test ne peut la tenir que si elle est
 * atteignable sans React et sans SDK.
 */
export function argumentsDeListe<T>(carte: LinkedCardDef<T>, id: string): ArgumentsDeListe {
  return { ...cadrageDeLien(carte.link, id), limit: carte.limit };
}

/* ------------------------------------------------------------------ */
/* Le sens direct : la liste de la fiche OUVERTE                       */
/* ------------------------------------------------------------------ */

/**
 * LES IDS D'UNE LISTE, quelle que soit la forme qui les porte : le tableau du
 * document (`customFields.produitsDemandes`), ou la cellule projetee (un id par
 * ligne). Sans doublon, dans l'ordre de la premiere apparition, jamais de blanc.
 *
 * FONCTION PURE ET EXPORTEE EXPRES : c'est ce qui decide quelles fiches la carte
 * LIT (sens direct) et ce que « Retirer » ecrit, et un id perdu ici se lit
 * comme une fiche qui n'a jamais ete liee.
 */
export function idsDeLaListe(valeur: unknown): string[] {
  const elements: unknown[] =
    Array.isArray(valeur) ? valeur : typeof valeur === 'string' ? valeur.split(/\r?\n/) : [];
  const vus: string[] = [];
  for (const element of elements) {
    if (typeof element !== 'string') continue;
    const id = element.trim();
    if (id !== '' && !vus.includes(id)) vus.push(id);
  }
  return vus;
}

/** La liste apres l'AJOUT d'un id : sans doublon, `null` quand elle ne change pas. */
export function listeApresAjout(actuelle: unknown, valeur: string): string[] | null {
  const ids = idsDeLaListe(actuelle);
  return ids.includes(valeur) ? null : [...ids, valeur];
}

/** La liste apres le RETRAIT d'un id : `null` quand l'id n'y etait pas. */
export function listeApresRetrait(actuelle: unknown, valeur: string): string[] | null {
  const ids = idsDeLaListe(actuelle);
  return ids.includes(valeur) ? ids.filter((id) => id !== valeur) : null;
}

/**
 * LE PATCH QUI AJOUTE OU RETIRE UN ID DANS LA LISTE D'UNE FICHE, ou `null` quand
 * il n'y a rien a ecrire. C'est ce que l'hote envoie au `update` du SDK apres
 * avoir lu la fiche (`ajouterALaListe`, `retirerDeLaListe`) : la liste
 * recalculee, rangee la ou le champ vit (`patchDeRattachement`).
 *
 * `null` EST UNE REPONSE, PAS UN ECHEC : ajouter un id deja la, retirer un id
 * absent, ne doit pas ecrire (un `updatedAt` de plus sur une fiche inchangee).
 */
export function patchDeListe(
  fiche: Record<string, unknown>,
  chemin: string,
  valeur: string,
  geste: 'ajouter' | 'retirer',
): Record<string, unknown> | null {
  const actuelle = lireChemin(fiche, chemin);
  const suivante = geste === 'ajouter' ? listeApresAjout(actuelle, valeur) : listeApresRetrait(actuelle, valeur);
  return suivante === null ? null : patchDeRattachement(chemin, suivante);
}

/**
 * UNE LISTE S'ECRIT-ELLE DEPUIS CETTE CARTE ? La regle des gestes de l'appartenance
 * et du sens direct, ECRITE UNE FOIS.
 *
 *  - EGALITE : oui, le droit de la porte decide ailleurs (inchange) ;
 *  - APPARTENANCE : oui hors lecture seule ;
 *  - SENS DIRECT : hors lecture seule ET si la fiche sait ecrire la colonne de la
 *    liste (`avecEcrivain` : colonne presente au catalogue de la fiche, avec un
 *    `set`). Sans cela, la liste se lit, elle ne s'ecrit pas.
 *
 * FONCTION PURE ET EXPORTEE EXPRES : la regle se trompe en SILENCE (un bouton
 * qui manque ne fait rougir aucun build), et la carte est un composant.
 */
export function ecritLesListes(
  lien: LinkedDoor,
  lectureSeule: string | undefined,
  avecEcrivain: boolean,
): boolean {
  if (estLienParListe(lien)) return lectureSeule === undefined && avecEcrivain;
  if (lien.multiple === true) return lectureSeule === undefined;
  return true;
}

/** Ce que la carte du sens direct a lu : les fiches gardees, et le compte. */
export interface LectureDeLaListe {
  records: Array<Record<string, unknown>>;
  /** Le nombre de fiches que la liste cite : celles qu'on lit, ou le nombre d'ids au-dela du plafond. */
  total: number;
  /** La liste depasse le plafond : seules les `limit` premieres sont lues. */
  partielle: boolean;
}

/**
 * LIT LES FICHES QUE LA LISTE CITE, par leurs ids (`LiensPort.lireParIds`).
 *
 * LES FICHES ARCHIVEES, SUPPRIMEES OU D'UN AUTRE SOUS-TYPE SONT ECARTEES : la
 * liste est un champ libre, un id y survit a l'archivage de la fiche qu'il
 * designe, et la carte montre les fiches VIVANTES du jeu lie, comme celle d'une
 * egalite. L'ORDRE EST CELUI DE LA LISTE (c'est la fiche ouverte qui le dit).
 *
 * LE PLAFOND S'APPLIQUE AUX IDS : au-dela de `limit`, on en lit `limit` et on le
 * dit (`partielle`) ; le compte est alors le nombre d'ids, le seul fait connu.
 * Dedans, le compte est celui des fiches gardees, pour qu'il ne contredise
 * jamais les lignes affichees.
 */
export async function lireLaListe(
  port: LiensPort,
  lien: LienParLaListe,
  ids: readonly string[],
  limit: number,
): Promise<LectureDeLaListe> {
  const uniques = idsDeLaListe([...ids]);
  const lus = uniques.slice(0, limit);
  const trouvees = lus.length === 0 ? [] : await port.lireParIds(lien.family, lus);
  const parId = new Map<string, Record<string, unknown>>();
  for (const fiche of trouvees) {
    const id = fiche['id'];
    if (typeof id === 'string') parId.set(id, fiche);
  }
  const records: Array<Record<string, unknown>> = [];
  for (const id of lus) {
    const fiche = parId.get(id);
    if (fiche === undefined) continue;
    if (fiche['archivedAt'] != null || fiche['deletedAt'] != null) continue;
    if (lien.type !== undefined && fiche['type'] !== lien.type) continue;
    records.push(fiche);
  }
  const partielle = uniques.length > limit;
  return { records, total: partielle ? uniques.length : records.length, partielle };
}

/**
 * LES TOTAUX DE PIED D'UNE CARTE DU SENS DIRECT, calcules sur les fiches lues :
 * une somme ou une moyenne par colonne qui en declare une.
 *
 * RIEN SI LA LISTE DEPASSE LE PLAFOND (`null`, que `totalDePied` rend comme
 * « rien a montrer ») : une somme sur 500 fiches parmi 800 est un nombre
 * plausible et faux. Une moyenne sans valeur numerique est `null` aussi, et ne
 * devient pas zero.
 */
export function totauxDeLaListe(
  totaux: ReadonlyArray<{ key: string; agg: 'count' | 'sum' | 'avg'; field?: string }>,
  lecture: LectureDeLaListe,
): Record<string, TotalServeur> {
  const sortie: Record<string, TotalServeur> = {};
  for (const entree of totaux) {
    if (entree.agg === 'count') {
      sortie[entree.key] = lecture.partielle ? null : lecture.records.length;
      continue;
    }
    if (lecture.partielle || entree.field === undefined) {
      sortie[entree.key] = null;
      continue;
    }
    sortie[entree.key] = agreger(lecture.records, entree.agg, entree.field);
  }
  return sortie;
}

/** Somme ou moyenne d'un champ numerique sur des fiches lues ; `null` sans aucune valeur (moyenne), 0 (somme). */
function agreger(
  records: readonly Record<string, unknown>[],
  agg: 'sum' | 'avg',
  champ: string,
): number | null {
  let somme = 0;
  let nombre = 0;
  for (const record of records) {
    const valeur = lireChemin(record, champ);
    if (!estNombre(valeur)) continue;
    somme += valeur;
    nombre += 1;
  }
  if (agg === 'sum') return Math.round(somme * 1e8) / 1e8;
  return nombre === 0 ? null : Math.round((somme / nombre) * 1e8) / 1e8;
}

/**
 * LE TOTAL D'UN CHAMP DE TOTAL SUR UN LIEN DU SENS DIRECT, en memoire sur les
 * fiches lues par leurs ids. Au-dela de `PLAFOND_TOTAL_PONDERE` ids, il est
 * REFUSE comme le total pondere (`ErreurDeTotalPondere`) : un nombre calcule sur
 * une partie de la liste serait plausible et faux.
 */
export async function lireTotalDeLaListe(
  port: LiensPort,
  rollup: RollupFieldDef,
  lien: LienParLaListe,
  ids: readonly string[],
): Promise<number | null> {
  const uniques = idsDeLaListe([...ids]);
  if (uniques.length > PLAFOND_TOTAL_PONDERE) {
    throw new ErreurDeTotalPondere(
      `Plus de ${PLAFOND_TOTAL_PONDERE} fiches dans la liste : le total n'est pas calcule plutot que faux.`,
    );
  }
  const lecture = await lireLaListe(port, lien, uniques, PLAFOND_TOTAL_PONDERE);
  if (rollup.agg === 'count') return lecture.records.length;
  if (rollup.field === undefined) return null;
  return agreger(lecture.records, rollup.agg, rollup.field);
}

/* ------------------------------------------------------------------ */
/* Supprimer une fiche liee : refuser, a la Odoo                       */
/* ------------------------------------------------------------------ */

/**
 * LE REFUS DE SUPPRIMER UNE FICHE QUI EN TIENT D'AUTRES (fondateur, 09/09/2026).
 *
 * LA DECISION, en une phrase : supprimer une fiche qui a des fiches liees est
 * REFUSE, avec le COMPTE, AVANT toute fenetre ; archiver ne l'est pas, mais la
 * confirmation dit combien de fiches restent accrochees. C'est le comportement
 * d'Odoo, et c'est le seul des trois possibles qui ne fabrique pas de donnee
 * orpheline : effacer en cascade detruit un travail que personne n'a demande
 * d'effacer, et detacher en silence laisse des interventions sans materiel que
 * plus aucun ecran ne remonte.
 *
 * UNE ARCHIVE COMPTE, UNE SUPPRIMEE NON. Archiver n'est pas detacher : une
 * intervention archivee pointe toujours son materiel, et l'effacer laisserait
 * exactement l'orphelin qu'on refuse. Une fiche supprimee, elle, n'existe plus
 * pour personne : elle n'entre dans aucun annuaire, et sa reference se dit
 * « Supprimee ou introuvable ». D'ou `archived: 'all'` et le
 * `deletedAt == null` que le SDK pose au serveur sur ce mode.
 *
 * AUCUNE DECLARATION NOUVELLE : la politique est le DEFAUT, pour toute porte qui
 * declare des liens. Une politique par lien (« celui-la, on detache ») viendra
 * si un cas reel la demande ; l'inventer avant aurait fait ecrire une clef que
 * personne ne sait remplir.
 */

/** Ce qu'une porte liee tient d'une fiche : son libelle, et le compte. */
export interface CompteLie {
  /** Le libelle de la porte liee, tel que le menu des portes l'affiche. */
  label: string;
  /** Les fiches liees, ARCHIVES COMPRISES, supprimees exclues. */
  count: number;
}

/**
 * LES COMPTES D'UNE FICHE, UN PAR LIEN, DEMANDES AU SERVEUR.
 *
 * EN PARALLELE, et pas l'un apres l'autre : ces comptes vivent entre le clic et
 * la fenetre, donc leur duree est celle du plus LENT, pas celle de leur somme.
 * Une porte qui declare quatre liens ne fait pas attendre quatre fois.
 *
 * IL NE RATTRAPE AUCUNE ERREUR, et c'est voulu : un `count` qui echoue doit
 * REFUSER la suppression (on ne prouve pas une absence avec une lecture qui n'a
 * pas eu lieu), et c'est l'appelant qui sait poser ce refus a l'ecran. Avaler
 * l'echec en rendant zero ferait exactement l'inverse : la suppression
 * passerait.
 *
 * `labelDePorte` VIENT DE L'APPELANT parce que le socle des liens ne connait pas
 * les portes de l'ecran : il traduit un `link.door` en libelle
 * (`ScreenDoor.label`), avec repli sur l'id.
 */
export async function comptesLies(
  port: LiensPort,
  links: readonly LinkedDoor[],
  id: string,
  labelDePorte: (door: string) => string,
): Promise<CompteLie[]> {
  /* L'EGALITE ET L'APPARTENANCE COMPTENT : supprimer la fiche laisserait un id
     orphelin dans la liste de chaque fiche liee (ou un `assetId` pendant). LE
     SENS DIRECT NE COMPTE PAS : la reference vit dans la fiche qu'on supprime,
     elle part avec elle. */
  const comptes = links.filter((lien): lien is LienParLeChampLie => !estLienParListe(lien));
  return await Promise.all(
    comptes.map(async (lien) => ({
      label: labelDePorte(lien.door),
      count: await port.count(lien.family, { ...cadrageDeLien(lien, id), archived: 'all' }),
    })),
  );
}

/**
 * LE TOTAL ET LES PORTES NON NULLES, dans l'ordre des liens.
 *
 * LES DOUBLONS TOMBENT : deux liens peuvent viser la MEME porte par deux champs
 * (un materiel « pose » et un materiel « repris »), et « (Interventions,
 * Interventions) » se lit comme une faute d'affichage. Le total, lui, les
 * additionne : ce sont bien deux fiches accrochees.
 */
function bilanDesLies(comptes: readonly CompteLie[]): { total: number; portes: string[] } {
  let total = 0;
  const portes: string[] = [];
  for (const compte of comptes) {
    if (compte.count <= 0) continue;
    total += compte.count;
    if (!portes.includes(compte.label)) portes.push(compte.label);
  }
  return { total, portes };
}

/** « 3 fiches liees (Interventions) » / « 1 fiche liee (Interventions) ». */
function fichesLiees(total: number, portes: readonly string[]): string {
  const nom = total > 1 ? `${total} fiches liees` : '1 fiche liee';
  return `${nom} (${portes.join(', ')})`;
}

/**
 * LA RAISON DU REFUS, ou `null` quand rien n'est accroche.
 *
 * ELLE DIT LE COMPTE ET LES DEUX SORTIES (G16 : un geste refuse dit ce qui le
 * leve). « Archive cette fiche » est la sortie qui garde tout, « rattache-les
 * ailleurs » celle qui libere : deux gestes que l'utilisateur sait faire, pas
 * une explication de mecanique interne.
 *
 * FONCTION PURE ET EXPORTEE EXPRES : l'accord (« 1 fiche liee ... rattache-la »
 * contre « 3 fiches liees ... rattache-les ») ne se voit qu'a l'ecran, et
 * seulement le jour ou une fiche n'en tient qu'une.
 */
export function refusDeSuppression(comptes: readonly CompteLie[]): string | null {
  const { total, portes } = bilanDesLies(comptes);
  if (total === 0) return null;
  const pronom = total > 1 ? 'rattache-les' : 'rattache-la';
  return `Suppression refusee : ${fichesLiees(total, portes)}. Archive cette fiche, ou ${pronom} ailleurs.`;
}

/**
 * LE BOUT DE PHRASE QUE LA CONFIRMATION D'ARCHIVAGE AJOUTE, ou `''` quand rien
 * n'est accroche.
 *
 * ARCHIVER N'EST PAS REFUSE, et cette phrase dit pourquoi ca ne casse rien : les
 * fiches liees RESTENT VISIBLES, elles ne partent pas avec le parent. C'est la
 * seule question qu'on se pose devant ce bouton quand une carte liee est ouverte
 * juste dessous.
 */
export function phraseDesLies(comptes: readonly CompteLie[]): string {
  const { total, portes } = bilanDesLies(comptes);
  if (total === 0) return '';
  const verbe = total > 1 ? 'restent visibles' : 'reste visible';
  return `${fichesLiees(total, portes)} ${verbe}.`;
}

/* ------------------------------------------------------------------ */
/* Ce qu'on donne a la vue montee dans la carte                        */
/* ------------------------------------------------------------------ */

/**
 * LE MEME BINDING, SANS UNE SEULE ECRITURE.
 *
 * UNE CARTE LIEE EST EN LECTURE (PR 1), et ca ne se tient pas en n'appelant pas
 * `onPatchRecord` : ce qui ouvre une case, c'est `ProjectedRow.editable` et
 * `cells[].editable`, tous deux calcules par la projection sur la PRESENCE d'un
 * `set()`. Retirer les `set` du binding avant de projeter est donc la seule
 * facon de rendre une case reellement inerte : sans ca, la fiche liee du
 * tableau proposerait ses editeurs, et une ecriture partirait sur un jeu que
 * cette carte ne sait pas recharger.
 *
 * NE MUTE RIEN : le binding vient de l'assemblage de la porte liee, que le
 * moteur emploie aussi pour l'ecran de cette porte. Le muter y eteindrait
 * l'edition, a distance.
 */
export function sansEcriture<T>(binding: ViewBinding<T>): ViewBinding<T> {
  const roles: Partial<Record<FieldRole, FieldMap<T>>> & Record<string, FieldMap<T>> = {};
  for (const [nom, champ] of Object.entries(binding.roles) as [string, FieldMap<T>][]) {
    if (champ == null) continue;
    const { set: _ecriture, ...lecture } = champ;
    roles[nom] = lecture;
  }
  const nu: ViewBinding<T> = { type: binding.type, roles };
  if (binding.columns !== undefined) {
    nu.columns = binding.columns.map((colonne) => {
      const { set: _ecriture, ...lecture } = colonne;
      return lecture;
    });
  }
  if (binding.options !== undefined) nu.options = binding.options;
  return nu;
}

/**
 * LA COLONNE QUI REDIT LE TITRE DE LA FICHE OUVERTE, RETIREE.
 *
 * LE FAIT. Le binding d'une carte liee est celui de la porte LIEE, references
 * comprises : sur la fiche d'un materiel, la carte « Interventions » monte le
 * tableau des interventions, et ce tableau declare la colonne « Materiel ».
 * Chaque ligne y afficherait le nom du materiel SOUS LEQUEL ON EST DEJA. Une
 * colonne dont toutes les cases portent le titre ecrit juste au-dessus
 * n'apprend rien et mange la largeur des colonnes qui, elles, disent quelque
 * chose.
 *
 * SUR LA PORTE, PAS SUR LE CHAMP : c'est `reference.door` qu'on compare a la
 * porte hote (`LinkedCardDef.porte`). Le champ du lien (`link.by`) dirait
 * presque la meme chose et se tromperait des qu'une porte designe une autre
 * porte par un second champ.
 *
 * LES AUTRES REFERENCES RESTENT, et elles sont le vrai gain de l'etage 1 : une
 * intervention qui designerait aussi un chantier montre le NOM de ce chantier.
 *
 * NE MUTE RIEN, et rend le binding TEL QUEL quand il n'y a rien a retirer :
 * l'identite est ce qui evite a la carte de reprojeter a chaque rendu.
 *
 * FONCTION PURE ET EXPORTEE EXPRES : une colonne de trop est un defaut qu'on lit
 * sans le voir (« pourquoi cette colonne est-elle toujours la meme ? »), et
 * aucun build ne la compte.
 */
export function sansColonneVersLaPorte<T>(
  binding: ViewBinding<T>,
  porte: string,
): ViewBinding<T> {
  if (porte === '' || binding.columns === undefined) return binding;
  const gardees = binding.columns.filter((colonne) => colonne.reference?.door !== porte);
  if (gardees.length === binding.columns.length) return binding;
  return { ...binding, columns: gardees };
}

/**
 * LE TITRE QUI REDIT LA FICHE OUVERTE : celui d'une ligne liee qui designe la
 * porte hote (la tache d'une saisie, sur la fiche de cette tache). Meme regle
 * que `sansColonneVersLaPorte`, pour le role `title` : la carte le masque
 * (`hideTitle`, `TableView`), la pastille « Ouvrir » se pose alors sur la premiere
 * colonne.
 */
export function titreVersLaPorte<T>(binding: ViewBinding<T>, porte: string): boolean {
  const parts = binding.roles.title?.parts;
  return porte !== '' && parts !== undefined && parts.length === 1 && parts[0]?.reference?.door === porte;
}

/**
 * LES COLONNES QUI REDISENT CE QUE LA FICHE OUVERTE ECRIT DEJA, RETIREES.
 *
 * LE FAIT (retour du fondateur du 14/10/2026, fiche d'une tache) : la carte
 * « Heures » d'une tache montrait le Projet de chaque saisie, ecrit en toutes
 * lettres juste au-dessus, sur la fiche. Une colonne de REFERENCE est retiree
 * quand la fiche ouverte designe la meme porte et que CHAQUE ligne chargee y
 * porte la meme fiche qu'elle : c'est la donnee qui le dit, jamais un nom de
 * champ. Une seule ligne differente, ou aucune ligne, et la colonne reste.
 *
 * @param redites la fiche ouverte : porte designee -> id de la fiche designee.
 */
export function sansColonnesRedites(
  colonnes: ViewColumn[],
  lignes: readonly ProjectedRow[],
  redites: Readonly<Record<string, string>>,
): ViewColumn[] {
  if (lignes.length === 0) return colonnes;
  const gardees = colonnes.filter((colonne) => {
    const porte = colonne.reference?.door;
    const id = porte === undefined ? undefined : redites[porte];
    return id === undefined || !lignes.every((ligne) => ligne.cells[colonne.key]?.value === id);
  });
  return gardees.length === colonnes.length ? colonnes : gardees;
}

/**
 * LA LIGNE RESTREINTE AUX COLONNES VISIBLES, GARDEE PAR LIGNE D'ORIGINE ET PAR
 * JEU DE COLONNES. `matchesSearch` met les textes normalises d'une ligne en
 * cache sur l'OBJET ligne (chantier `ecran-fluide`) : une ligne restreinte
 * refabriquee a chaque frappe serait un objet neuf a chaque lettre, donc
 * renormalisee a chaque lettre. Meme ligne et memes colonnes, meme objet.
 */
const LIGNES_RESTREINTES = new WeakMap<ProjectedRow, Map<string, ProjectedRow>>();

function ligneRestreinte(ligne: ProjectedRow, colonnes: readonly ViewColumn[]): ProjectedRow {
  const clef = colonnes.map((colonne) => colonne.key).join('\u0000');
  let parClef = LIGNES_RESTREINTES.get(ligne);
  if (parClef === undefined) {
    parClef = new Map();
    LIGNES_RESTREINTES.set(ligne, parClef);
  }
  const deja = parClef.get(clef);
  if (deja !== undefined) return deja;
  const cells: ProjectedRow['cells'] = {};
  for (const colonne of colonnes) {
    const cellule = ligne.cells[colonne.key];
    if (cellule !== undefined) cells[colonne.key] = cellule;
  }
  const restreinte: ProjectedRow = { ...ligne, cells };
  parClef.set(clef, restreinte);
  return restreinte;
}

/**
 * LA RECHERCHE D'UNE CARTE LIEE : les lignes dont le TITRE, le sous-titre, le
 * statut ou une colonne VISIBLE de la carte contient le terme. Meme regle que
 * l'ecran (`matchesSearch` : sans casse ni accents), restreinte a ce que la
 * carte MONTRE : chercher sur une colonne que `sansColonnesRedites` a retiree
 * (ou que la porte liee ne declare pas) trouverait une ligne pour un mot
 * qu'on ne voit nulle part.
 *
 * CHERCHE DANS LE JEU LU, pas sur le serveur : la carte ne charge qu'une page
 * (`limit`), et son compte serveur dit le reste. Terme vide ou blanc : les
 * lignes TELLES QUELLES (meme reference), de quoi ne rien rendre de plus quand
 * on ne cherche pas.
 */
export function lignesCherchees(
  rows: ProjectedRow[],
  colonnes: ViewColumn[],
  terme: string,
): ProjectedRow[] {
  if (terme.trim() === '') return rows;
  return rows.filter((ligne) => matchesSearch(ligneRestreinte(ligne, colonnes), terme));
}

/**
 * LE COMPTE DE L'EN-TETE D'UNE CARTE LIEE : le total des fiches liees, ou
 * « trouvees / total » pendant une recherche (`trouvees` nul = on ne cherche
 * pas). Meme separateur que le pied « chargees / total » de la carte, et meme
 * formateur que tous les nombres de la carte (`formaterNombre`).
 */
export function compteDeLaCarte(total: number, trouvees: number | null): string {
  return trouvees === null
    ? formaterNombre(total)
    : `${formaterNombre(trouvees)} / ${formaterNombre(total)}`;
}

/**
 * LES LIGNES, TRIEES PAR LA PILE : le niveau 1 commande, les suivants
 * departagent ses egalites, et l'ordre d'arrivee fait foi sur les ex aequo
 * (`Array.prototype.sort` est stable).
 *
 * MEME COMPARATEUR QUE L'ECRAN (`sortValueOf` + `compareCells`) : une clef que
 * le binding ne resout pas ne deplace rien, exactement comme dans une liste
 * ordinaire. Le tri par en-tete du tableau arrive ici, et nulle part ailleurs :
 * ni la carte liee ni la fenetre de choix ne rechargent, elles retrient ce
 * qu'elles ont.
 *
 * ELLE VIT DANS LE SOCLE DES LIENS, pas dans la carte, depuis que la FENETRE DE
 * CHOIX trie elle aussi ses candidates : deux copies « identiques au
 * comparateur pres » auraient range deux tables de la meme porte dans deux
 * ordres differents, le jour ou l'une des deux est retouchee.
 *
 * NE MUTE RIEN, et rend le tableau TEL QUEL quand il n'y a rien a trier :
 * l'identite est ce qui evite un rendu de plus.
 */
export function trierLignes(
  rows: ProjectedRow[],
  pile: readonly SortLevel[],
): ProjectedRow[] {
  if (pile.length === 0) return rows;
  return [...rows].sort((a, b) => {
    for (const niveau of pile) {
      const compare = compareCells(sortValueOf(a, niveau.key), sortValueOf(b, niveau.key));
      if (compare !== 0) return niveau.dir === 'desc' ? -compare : compare;
    }
    return 0;
  });
}

/**
 * LES COLONNES TELLES QUE LA VUE LES RECOIT : les metadonnees, jamais les
 * fonctions.
 *
 * UNE SEULE TRADUCTION POUR TOUT LE PAQUET : le moteur d'ecran l'appelle aussi
 * (`views/useScreen.ts`, memo `projected`). Elle a vecu en deux copies, « la
 * meme au champ pres », jusqu'a ce que la REFERENCE d'une colonne doive
 * traverser la frontiere : une seule des deux l'aurait posee, et la fiche
 * aurait su servir son annuaire la ou le tableau ne l'aurait pas su.
 *
 * LA REFERENCE PASSE EN DONNEE, PAS EN FONCTION : `titleOf` et `subtitleOf`
 * restent cote core (ils remplissent l'annuaire), la vue ne recoit que de quoi
 * retrouver cet annuaire et nommer la porte de la fiche liee.
 */
export function colonnesDeVue<T>(
  binding: ViewBinding<T> | undefined,
  /** En pagination SERVEUR, une colonne que le serveur ne sait pas trier (`Column.triServeur`) n'est pas triable. */
  pagination: 'client' | 'server' = 'client',
): ViewColumn[] {
  return (binding?.columns ?? []).map((colonne) => ({
    key: colonne.key,
    label: colonne.label,
    sortable: (colonne.sortable ?? false) && !(pagination === 'server' && colonne.triServeur === false),
    ...(colonne.lueSur === undefined ? {} : { lueSur: colonne.lueSur }),
    ...(colonne.agg === undefined ? {} : { agg: colonne.agg }),
    /* L'HABILLAGE DE L'AGREGAT VOYAGE AVEC LUI : sans cette ligne, le pied d'une
       colonne en euros retombait sur le nombre nu, six lignes sous des cases
       qui portent le signe. */
    ...(colonne.formatAgg === undefined ? {} : { formatAgg: colonne.formatAgg }),
    ...(colonne.totalLie === true ? { totalLie: true as const } : {}),
    editable: typeof colonne.set === 'function',
    ...(colonne.kind === undefined ? {} : { kind: colonne.kind }),
    /* LE TYPE ET LES REGLAGES DU CHAMP PASSENT EN DONNEE : la fiche pose son
       interrupteur, son suffixe « m² », ses etoiles, et le pied d'un montant se
       somme au centime (`views/champs-types.ts`). */
    ...(colonne.valueType === undefined ? {} : { valueType: colonne.valueType }),
    ...(colonne.reglages === undefined ? {} : { reglages: colonne.reglages }),
    /* LE CHEMIN DE STOCKAGE PASSE, LUI AUSSI, EN DONNEE : c'est par lui que la
       fiche sait si un champ supporte d'etre vide (`proposeDetacher`,
       `views/core/RecordView.tsx`). Le rederiver cote vue redonnerait une
       seconde regle « natif ou sac », qui divergerait de `cheminDeStockage`. */
    ...(colonne.path === undefined ? {} : { path: colonne.path }),
    /* LE PLACEHOLDER ET L'OBLIGATION PASSENT AUSSI : ce sont les deux seules
       choses qui font qu'une case VIDE ne se rend pas pareil sur une fiche
       existante et sur un brouillon de creation. Sans eux, la fiche de creation
       dirait « Non renseigne » partout et ne saurait pas quelle case est
       obligatoire. */
    ...(colonne.placeholder === undefined ? {} : { placeholder: colonne.placeholder }),
    ...(colonne.required === true ? { required: true } : {}),
    ...(colonne.reference === undefined
      ? {}
      : {
          reference: {
            ...(colonne.reference.door === undefined ? {} : { door: colonne.reference.door }),
            family: colonne.reference.family,
            ...(colonne.reference.type === undefined ? {} : { type: colonne.reference.type }),
            ...(colonne.reference.modeles === true ? { modeles: true } : {}),
          },
        }),
  }));
}

/* ------------------------------------------------------------------ */
/* Les totaux : le pied du tableau, et le champ de total de la fiche   */
/* ------------------------------------------------------------------ */

/**
 * UN NOMBRE AFFICHE : separateurs du poste, deux decimales au plus.
 *
 * UN SEUL FORMAT POUR LES DEUX BOUTS DU MEME CHIFFRE : le pied d'une colonne
 * (fenetre ou serveur) et le champ de total d'une fiche montrent la meme somme,
 * lue au meme endroit. Deux formateurs auraient diverge au premier reglage, et
 * la fiche aurait affiche un autre nombre que son tableau.
 */
export function formaterNombre(valeur: number): string {
  return valeur.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

/**
 * COMBIEN DE LIGNES UN TOTAL PONDERE LIT, au plus : le plafond dur d'une page du
 * SDK (`MAX_LIST_LIMIT`, `packages/sdk/src/list-query.ts`). Au-dela, le total
 * est REFUSE avec sa raison (`ErreurDeTotalPondere`), jamais calcule sur une
 * partie : un prix sur 1000 lignes parmi 1200 serait un nombre plausible et
 * faux.
 */
export const PLAFOND_TOTAL_PONDERE = 1000;

/** Le refus d'un total pondere trop gros pour etre calcule exactement. */
export class ErreurDeTotalPondere extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ErreurDeTotalPondere';
  }
}

const lireChemin = (document: unknown, chemin: string): unknown =>
  chemin.split('.').reduce<unknown>(
    (valeur, clef) =>
      valeur !== null && typeof valeur === 'object' ? (valeur as Record<string, unknown>)[clef] : undefined,
    document,
  );

const estNombre = (valeur: unknown): valeur is number =>
  typeof valeur === 'number' && Number.isFinite(valeur);

/** Les ids DISTINCTS que les lignes designent, dans l'ordre ou elles les citent. */
export function idsDesignes(
  lignes: readonly Record<string, unknown>[],
  by: string,
): string[] {
  const vus = new Set<string>();
  for (const ligne of lignes) {
    const id = lireChemin(ligne, by);
    if (typeof id === 'string' && id !== '') vus.add(id);
  }
  return [...vus];
}

/**
 * LE TOTAL PONDERE : la somme, sur les lignes, de `valeur de la ligne x nombre
 * de la fiche qu'elle designe` (« quantite x prix TTC de l'article »).
 *
 * FONCTION PURE ET EXPORTEE EXPRES : le resultat est un nombre plausible a
 * l'ecran, et seul un test tient la regle.
 *
 * LA REGLE DE LA LIGNE QUI NE COMPTE PAS (`appScreenPonderationSchema`) : une
 * ligne ne compte pas quand sa valeur n'est pas un nombre, quand elle ne
 * designe aucune fiche, quand la fiche designee est introuvable, SUPPRIMEE
 * (`deletedAt`) ou d'un autre sous-type que la reference, ou quand elle n'a pas
 * le nombre. Une fiche designee ARCHIVEE, elle, compte : archiver un article ne
 * change pas le prix des lignes qui le citent. Aucune ligne ne compte : la
 * somme vaut 0, comme celle du serveur sans fiche.
 *
 * LE RESULTAT EST ARRONDI A 8 DECIMALES : 3 x 0,1 ne doit pas s'afficher
 * 0,30000000000000004 le jour ou un format ne l'arrondit pas.
 */
export function totalPondere(
  lignes: readonly Record<string, unknown>[],
  designees: ReadonlyMap<string, Record<string, unknown>>,
  regle: { valeur: string; by: string; field: string; type?: string },
): number {
  let somme = 0;
  for (const ligne of lignes) {
    const valeur = lireChemin(ligne, regle.valeur);
    const id = lireChemin(ligne, regle.by);
    if (!estNombre(valeur) || typeof id !== 'string' || id === '') continue;
    const fiche = designees.get(id);
    if (fiche === undefined || fiche['deletedAt'] != null) continue;
    if (regle.type !== undefined && fiche['type'] !== regle.type) continue;
    const nombre = lireChemin(fiche, regle.field);
    if (!estNombre(nombre)) continue;
    somme += valeur * nombre;
  }
  return Math.round(somme * 1e8) / 1e8;
}

/**
 * LIT ET CALCULE UN TOTAL PONDERE, par le port : les lignes du lien (une lecture
 * cadree par la fiche ouverte), puis les fiches designees EN UN LOT (une lecture
 * par id distinct, `LiensPort.lireParIds`), puis `totalPondere`.
 *
 * LE COMPTE RESTE EXACT, OU IL EST REFUSE. Une page pleine n'est pas un total :
 * quand les lignes lues atteignent le plafond, `count()` (serveur) dit s'il en
 * existe davantage, et le total est alors refuse avec sa raison.
 */
export async function lireTotalPondere(
  port: LiensPort,
  rollup: RollupFieldDef,
  cadrage: CadrageDeLien,
): Promise<number> {
  const times = rollup.times;
  if (times === undefined || rollup.field === undefined) {
    throw new ErreurDeTotalPondere('Total pondere sans pondération ni champ.');
  }
  const lignes = await port.list(rollup.link.family, {
    ...(cadrage.type === undefined ? {} : { type: cadrage.type }),
    where: cadrage.where,
    ...(cadrage.contient === undefined ? {} : { contient: cadrage.contient }),
    limit: PLAFOND_TOTAL_PONDERE,
  });
  if (lignes.length >= PLAFOND_TOTAL_PONDERE) {
    const existantes = await port.count(rollup.link.family, cadrage);
    if (existantes > lignes.length) {
      throw new ErreurDeTotalPondere(
        `Plus de ${PLAFOND_TOTAL_PONDERE} lignes : le total pondéré n'est pas calculé plutôt que faux.`,
      );
    }
  }
  const ids = idsDesignes(lignes, times.by);
  const fiches = ids.length === 0 ? [] : await port.lireParIds(times.family, ids);
  const designees = new Map<string, Record<string, unknown>>();
  for (const fiche of fiches) {
    const id = fiche['id'];
    if (typeof id === 'string') designees.set(id, fiche);
  }
  return totalPondere(lignes, designees, {
    valeur: rollup.field,
    by: times.by,
    field: times.field,
    ...(times.type === undefined ? {} : { type: times.type }),
  });
}

/** La clef du sac libre par laquelle les totaux SERVEUR arrivent au tableau. */
const CLEF_TOTAUX = 'totals';

/**
 * LE PIED D'UNE COLONNE : le total du SERVEUR quand l'hote en fournit un pour
 * cette clef, sinon le calcul sur la fenetre affichee.
 *
 * POURQUOI LES DEUX COEXISTENT. Le pied du tableau totalise « ce qu'on voit »,
 * et c'est juste sur une liste complete : la fenetre EST le jeu. Dans une carte
 * liee, plafonnee par construction, ce meme calcul affiche le total des 200
 * premieres lignes en le presentant comme LE total. Le symptome ne ressemble
 * pas a une panne, c'est un nombre plausible : d'ou un total demande au
 * serveur, et d'ou cette fonction, qui dit lequel des deux gagne.
 *
 * LE SERVEUR GAGNE DES QU'IL A PARLE, y compris avec un `null` : `average` rend
 * `null` quand aucune fiche ne repond, et retomber alors sur la fenetre
 * rendrait la moyenne des lignes chargees, c'est-a-dire l'inverse de ce que le
 * serveur vient de dire. Une clef ABSENTE du sac, elle, n'est pas une reponse :
 * l'ecran n'a rien demande, la fenetre reprend la main. C'est ce qui laisse les
 * ecrans existants (aucun `totals` dans leur sac) exactement comme avant.
 *
 * FONCTION PURE ET EXPORTEE EXPRES : le choix est invisible a l'ecran (les deux
 * branches rendent un nombre), et seul un test peut le tenir.
 */
export function totalDePied(
  options: Record<string, unknown>,
  key: string,
  fenetre: string | null,
  /* L'HABILLAGE DE LA COLONNE, quand elle en a un : le total du SERVEUR s'ecrit
     comme celui de la fenetre, sinon deux chiffres du meme mot se liraient dans
     deux unites selon que la liste tient ou non sur une page. */
  format?: (valeur: number) => string,
): { texte: string | null; serveur: boolean; attente?: true } {
  const totaux = options[CLEF_TOTAUX];
  if (typeof totaux === 'object' && totaux !== null && key in totaux) {
    const valeur = (totaux as Record<string, unknown>)[key];
    /* L'INDEX DU TOTAL SE CONSTRUIT : on le DIT, a la place du chiffre (G16).
       Retomber sur le total de la fenetre afficherait un chiffre faux sous le
       mot « Total », sans rien signaler. */
    if (valeur === TOTAL_EN_CONSTRUCTION) return { texte: LIBELLE_INDEX, serveur: true, attente: true };
    if (typeof valeur === 'number' && Number.isFinite(valeur)) {
      return {
        texte: format === undefined ? formaterNombre(valeur) : format(valeur),
        serveur: true,
      };
    }
    /* `null` (une moyenne sans fiche) ou une valeur qui n'est pas un nombre :
       le serveur a repondu « rien a montrer », et on ne montre rien. */
    if (valeur === null) return { texte: null, serveur: true };
  }
  return { texte: fenetre, serveur: false };
}

/* ------------------------------------------------------------------ */
/* L'index en construction                                             */
/* ------------------------------------------------------------------ */

/**
 * UN TOTAL DONT L'INDEX N'EST PAS PRET. Decision du fondateur du 04/10/2026
 * (« auto », `docs/CHANTIERS-SOCLE.md`) : l'index qu'une carte liee ou un total
 * exige est CREE a la publication de l'app, et l'ecran dit « index en
 * construction » tant qu'il n'est pas pret, puis le total apparait seul.
 *
 * UN SEUL ETAT POUR « ABSENT » ET « EN CONSTRUCTION », et ce n'est pas une
 * paresse : pendant que l'index se construit, Firestore repond EXACTEMENT le
 * meme message que s'il n'existait pas (releve sur prod-tac-studio le
 * 04/10/2026, index en `CREATING`). Rien ne permet de les distinguer cote
 * client ; et un index absent est, par la decision, un index en route.
 */
export const TOTAL_EN_CONSTRUCTION = 'index-en-construction' as const;

/** Ce que le serveur rend pour un total : un nombre, rien, ou l'attente de l'index. */
export type TotalServeur = number | null | typeof TOTAL_EN_CONSTRUCTION;

/** Le mot de l'attente, partout le meme. */
export const LIBELLE_INDEX = 'Index en construction';

/** La raison, au survol ou sous le titre : ce qui se passe et ce qui va suivre. */
export const RAISON_INDEX =
  "Firestore construit l'index que cette lecture exige. Elle se relance seule et le resultat apparaitra des qu'il sera pret, en general en quelques minutes.";

/** Toutes les combien une lecture en attente d'index se relance, en ms. */
export const RELANCE_INDEX_MS = 30_000;

/**
 * L'ERREUR EST-ELLE UN INDEX QUI MANQUE ? Le SDK enrichit l'erreur de Firestore
 * en `MissingIndexError` (`packages/sdk/src/list-query.ts`) ; le socle ne
 * dependant pas du SDK, il la reconnait par sa FORME (son nom, ou le
 * `failed-precondition` brut de Firestore et son message). Toute autre panne
 * reste une panne.
 */
export function estErreurDIndex(erreur: unknown): boolean {
  if (erreur === null || typeof erreur !== 'object') return false;
  const { name, code, message } = erreur as { name?: unknown; code?: unknown; message?: unknown };
  if (name === 'MissingIndexError') return true;
  return code === 'failed-precondition' && typeof message === 'string' && /requires an index/i.test(message);
}

/** Le sac libre d'un binding, plus les totaux SERVEUR que la carte a demandes. */
export function sacAvecTotaux(
  options: Record<string, unknown> | undefined,
  totaux: Record<string, TotalServeur>,
): Record<string, unknown> {
  return { ...(options ?? {}), [CLEF_TOTAUX]: totaux };
}

/* ------------------------------------------------------------------ */
/* Creer depuis la fiche parente : les valeurs imposees                */
/* ------------------------------------------------------------------ */

/**
 * UNE VALEUR QU'UNE CREATION RECOIT D'OFFICE.
 *
 * D'OU ELLE VIENT : le « + Ajouter » d'une carte liee ne cree rien la ou il est
 * (la fenetre de creation appartient a l'ECRAN de l'app, pas au socle). Il
 * PASSE dans la porte liee et y ouvre SA creation, avec le champ du lien
 * pre-rempli et verrouille : c'est le bouton intelligent d'Odoo. Ce que la
 * porte d'arrivee recoit, c'est donc ca, et rien d'autre.
 *
 * `chemin` EST UN CHEMIN DE STOCKAGE (`assetId`, `customFields.materielId`),
 * jamais un nom nu : c'est le `by` du lien, tel que l'assemblage l'a pose
 * (`LinkedDoor.by`). La creation le range la ou il vivra, et rederiver la regle
 * ici la ferait exister a deux endroits.
 *
 * `libelle` NE SE STOCKE PAS, il s'AFFICHE : c'est le titre de la fiche parente
 * (« Sony FX9 »), ce que la fenetre montre dans la case verrouillee. « Un lien
 * stocke un id, jamais un libelle recopie » : ce mot ne part jamais au serveur.
 */
export interface ValeurImposee {
  /** Le CHEMIN DE STOCKAGE du champ impose, dans le jeu qu'on cree. */
  chemin: string;
  /** Ce qui s'ecrit : l'id de la fiche parente. */
  valeur: unknown;
  /** Ce qui s'affiche dans la case verrouillee, quand l'appelant le connait. */
  libelle?: string;
}

/**
 * POSE LES VALEURS IMPOSEES DANS UNE ENTREE DE CREATION.
 *
 * FONCTION PURE ET EXPORTEE EXPRES : c'est le seul endroit du geste qui puisse
 * se tromper en SILENCE. Un champ sur-mesure pose a la RACINE part au serveur,
 * passe la validation (le sac est libre), et la fiche creee n'apparait dans
 * AUCUNE carte liee : le cadrage cherche `customFields.materielId`, le document
 * porte `materielId`. Rien ne plante, rien ne s'affiche.
 *
 * DEUX ESPACES, ET DEUX SEULEMENT (AGENTS.md section 7) : le sac `customFields`
 * pour un chemin qui le nomme, la racine pour tout le reste. Un chemin plus
 * profond n'existe pas dans le socle, et l'inventer ici en ferait une regle que
 * personne d'autre ne tient.
 *
 * LE SAC EST RECOPIE CLEF PAR CLEF, jamais remplace : les autres champs
 * sur-mesure que le formulaire a deja saisis survivent. Et la valeur IMPOSEE
 * gagne sur celle que l'entree portait : c'est tout le sens du mot, et la case
 * est verrouillee a l'ecran pour la meme raison.
 *
 * NE MUTE RIEN : l'entree vient souvent d'un etat React que d'autres relisent.
 */
export function appliquerValeursImposees(
  entree: Record<string, unknown>,
  defaults: readonly ValeurImposee[] | undefined,
): Record<string, unknown> {
  if (defaults === undefined || defaults.length === 0) return entree;
  const sortie = { ...entree };
  for (const { chemin, valeur } of defaults) {
    if (!chemin.startsWith(SAC_SUR_MESURE)) {
      sortie[chemin] = valeur;
      continue;
    }
    const precedent = sortie['customFields'];
    const sac =
      typeof precedent === 'object' && precedent !== null && !Array.isArray(precedent)
        ? (precedent as Record<string, unknown>)
        : {};
    sortie['customFields'] = { ...sac, [chemin.slice(SAC_SUR_MESURE.length)]: valeur };
  }
  return sortie;
}

/* ------------------------------------------------------------------ */
/* Lier une fiche existante : le patch, et les candidates              */
/* ------------------------------------------------------------------ */

/**
 * LE PATCH D'UN RATTACHEMENT : UNE seule clef, rangee la ou le champ vit.
 *
 * `{ productId: 'p1' }` pour un champ natif, `{ customFields: { fournisseurId:
 * 'f1' } }` pour un champ sur-mesure. Le SDK eclate ensuite le sac en chemins
 * pointes et le FUSIONNE clef par clef (`buildUpdateData`,
 * `docs/DONNEES-SDK.md`) : les autres champs sur-mesure de la fiche survivent,
 * et une app n'efface jamais les clefs d'une autre.
 *
 * IL NE REECRIT PAS LA REGLE « natif ou sac », IL LA REUTILISE : c'est
 * exactement celle de la creation depuis une fiche parente, donc le meme
 * `appliquerValeursImposees`, sur une entree vide. Deux copies auraient
 * diverge, et le symptome est MUET des deux cotes : une fiche rattachee par sa
 * clef nue passe la validation et n'apparait dans AUCUNE carte liee, parce que
 * le cadrage, lui, cherche `customFields.fournisseurId`.
 *
 * FONCTION PURE ET EXPORTEE EXPRES : c'est le seul endroit du geste qui puisse
 * se tromper en silence, et un test ne peut la tenir que sans React ni SDK.
 */
export function patchDeRattachement(
  chemin: string,
  valeur: string | readonly string[],
): Record<string, unknown> {
  /* UNE LISTE SE RANGE COMME UN ID : `appliquerValeursImposees` pose la valeur
     telle quelle a son chemin, natif a la racine, sur-mesure dans le sac. Un
     tableau est recopie, pour que le patch ne partage rien avec l'appelant. */
  return appliquerValeursImposees({}, [{ chemin, valeur: typeof valeur === 'string' ? valeur : [...valeur] }]);
}

/**
 * CE QUE PORTE UNE CANDIDATE DEJA RATTACHEE AILLEURS, ecrit ici et nulle part
 * ailleurs.
 *
 * POURQUOI CETTE MENTION EXISTE. Une reference est A VALEUR UNIQUE : la lier
 * ici la DETACHE de la fiche ou elle est. Sans ce mot, le geste retire en
 * silence une ligne d'une AUTRE fiche, que personne ne regarde a ce
 * moment-la, et le defaut ne se decouvre qu'en ouvrant l'autre fiche.
 *
 * UN LIBELLE, PAS UNE PHRASE (G26), et il ne dit PAS ou : le nom de l'autre
 * parent demanderait un annuaire de plus pour un mot qu'on ne lit qu'une fois.
 */
export const MENTION_DEJA_LIEE = 'Déjà liée ailleurs';

/** Ce que le socle met entre le sous-titre d'une fiche et cette mention. */
const SEPARATEUR_MENTION = ' · ';

/** Une fiche que « Lier une fiche existante » propose. */
export interface CandidatARattacher {
  /** L'id de la fiche a ecrire : c'est elle qui recevra le champ du lien. */
  id: string;
  /** Son titre, tel que la porte LIEE l'affiche dans ses propres listes. */
  label: string;
  /** Son sous-titre, la mention « Deja liee ailleurs », ou les deux. */
  sousTitre?: string;
  /** Elle pointe DEJA une autre fiche : la lier la DEPLACE. */
  dejaLiee: boolean;
}

/**
 * LA VALEUR D'UN CHEMIN DE STOCKAGE dans un document, en chaine.
 *
 * MEME REGLE QUE `appliquerValeursImposees`, DANS L'AUTRE SENS : ce qui s'ECRIT
 * dans le sac se LIT dans le sac. Lire `record['fournisseurId']` sur un champ
 * sur-mesure rend toujours `undefined`, donc toutes les fiches passeraient pour
 * libres et « Deja liee ailleurs » ne s'afficherait JAMAIS.
 *
 * TOUT CE QUI N'EST PAS UNE CHAINE NON VIDE VAUT « pas rattachee » : un id est
 * une chaine, et un `null` laisse par un detachement (`deleteField` cote SDK,
 * ou une valeur vide posee a la main) dit exactement la meme chose.
 */
function valeurAuChemin(record: Record<string, unknown>, chemin: string): string {
  if (!chemin.startsWith(SAC_SUR_MESURE)) {
    const lue = record[chemin];
    return typeof lue === 'string' ? lue : '';
  }
  const sac = record['customFields'];
  if (typeof sac !== 'object' || sac === null || Array.isArray(sac)) return '';
  const lue = (sac as Record<string, unknown>)[chemin.slice(SAC_SUR_MESURE.length)];
  return typeof lue === 'string' ? lue : '';
}

/**
 * LES FICHES QU'ON PEUT RATTACHER A CETTE FICHE-CI, dans l'ordre du lecteur.
 *
 * TROIS SORTS, ET ILS NE SE CONFONDENT PAS :
 *   - DEJA LIEE A CE PARENT : elle est DEHORS. Elle est deja dans la carte,
 *     juste dessous : la proposer offrirait un geste sans effet, et le clic ne
 *     changerait rien a l'ecran (meme valeur ecrite), ce qui se lit comme une
 *     panne.
 *   - LIEE AILLEURS : elle est DEDANS, avec sa mention (voir
 *     `MENTION_DEJA_LIEE`). C'est un geste legitime (« cette personne travaille
 *     en fait dans cette societe-la »), mais il DEPLACE, et ca doit se voir
 *     avant le clic.
 *   - LIBRE : elle est dedans, nue.
 *
 * LES ARCHIVES SONT DEHORS, et ce n'est pas un doublon de ce que le serveur
 * fait deja. L'hote ne demande pas les archives (`archived` absent = les
 * actives seules, `ArgumentsDeListe`), donc elles n'arrivent normalement pas
 * jusqu'ici ; le filtre tient quand meme, parce que rattacher une fiche sortie
 * du parc est exactement la donnee qu'on passe ensuite du temps a expliquer
 * (meme regle que `choixDeFicheLiee`, `views/core/RecordView.tsx`). Il n'y a
 * pas d'exception « celle qui est deja la » ici, contrairement a une case de
 * fiche : ce choix ne montre AUCUNE valeur courante, il propose des fiches a
 * rattacher.
 *
 * `libeller` VIENT DE L'APPELANT, ET C'EST LA MEME PROJECTION QUE LES RANGEES :
 * la carte projette ses lignes avec le binding de la porte liee, et une
 * candidate doit porter le mot qu'elle portera une fois rattachee. Un libelle
 * invente ici (« le champ `name` ») aurait fait choisir dans une liste qui ne
 * ressemble pas au resultat.
 *
 * PAR ORDRE ALPHABETIQUE, casse ignoree : la fenetre arrive dans l'ordre
 * NATUREL du serveur (aucun `orderBy`, voir `ArgumentsDeListe`), et une liste
 * qu'on parcourt de l'oeil se range par son mot.
 *
 * FONCTION PURE ET EXPORTEE EXPRES : les trois sorts se trompent en SILENCE
 * (une fiche qui manque ressemble a un jeu vide, une mention absente ne se
 * decouvre qu'en ouvrant l'autre fiche), et aucun build ne les compte.
 */
export function candidatsARattacher(
  records: readonly Record<string, unknown>[],
  lien: LinkedDoor,
  parentId: string,
  libeller: (record: Record<string, unknown>) => { title: string; subtitle?: string },
): CandidatARattacher[] {
  if (estLienParListe(lien)) {
    /* LE SENS DIRECT ECRIT LA FICHE OUVERTE : les candidates sont celles que SA
       liste ne cite pas encore (`candidatsPourUnChamp`), pas des enfants a
       rattacher. L'appelant a la liste, pas ce parametre. */
    throw new Error(
      `[taki/liens] Le lien "${lien.id}" se declare par "liste" : ses candidates se tirent de la liste ` +
        'de la fiche ouverte (`candidatsPourUnChamp`).',
    );
  }
  const candidats: CandidatARattacher[] = [];
  for (const record of records) {
    const id = record['id'];
    if (typeof id !== 'string' || id === '') continue;
    if (record['archivedAt'] != null) continue;
    /* L'APPARTENANCE : l'enfant porte une LISTE, et la fiche ouverte n'y est ou
       n'y est pas. Celle qui la contient deja est DEHORS (la choisir ne changerait
       rien) ; les autres sont LIBRES, sans mention : ajouter un id a une liste ne
       deplace rien, une fiche peut figurer dans autant de listes qu'on veut. */
    if (lien.multiple === true) {
      if (idsDeLaListe(lireChemin(record, lien.by)).includes(parentId)) continue;
      const { title, subtitle } = libeller(record);
      candidats.push({
        id,
        label: title,
        dejaLiee: false,
        ...(subtitle === undefined || subtitle === '' ? {} : { sousTitre: subtitle }),
      });
      continue;
    }
    /* `lien.by` EST DEJA UN CHEMIN DE STOCKAGE, pose par l'assemblage : on le
       prend TEL QUEL, exactement comme `cadrageDeLien`. */
    const attachee = valeurAuChemin(record, lien.by);
    if (attachee !== '' && attachee === parentId) continue;
    const { title, subtitle } = libeller(record);
    const dejaLiee = attachee !== '';
    const propre = subtitle === undefined || subtitle === '' ? '' : subtitle;
    /* LES DEUX SE CUMULENT plutot que de s'ecraser : le sous-titre de la porte
       liee est souvent ce qui SEPARE deux fiches de meme nom, et la mention est
       ce qui dit que le geste deplace. Perdre l'un pour montrer l'autre ferait
       choisir entre « laquelle » et « quel effet ». */
    const sousTitre = !dejaLiee
      ? propre
      : propre === ''
        ? MENTION_DEJA_LIEE
        : `${propre}${SEPARATEUR_MENTION}${MENTION_DEJA_LIEE}`;
    candidats.push({
      id,
      label: title,
      dejaLiee,
      ...(sousTitre === '' ? {} : { sousTitre }),
    });
  }
  return candidats.sort((a, b) =>
    a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' }),
  );
}

/**
 * LES FICHES QUE « Lier une fiche existante » PROPOSE A UN CHAMP DE LIEN, le
 * sens DIRECT de `candidatsARattacher` (chantier `champ-lie-deux-gestes`).
 *
 * LA MEME REGLE, LUE DANS L'AUTRE SENS :
 *   - LA FICHE DEJA POSEE DANS LE CHAMP est DEHORS, comme une fiche deja liee a
 *     ce parent dans la carte : la choisir ne changerait rien, et un geste sans
 *     effet se lit comme une panne. Le champ la montre deja ;
 *   - LES ARCHIVES sont dehors (on ne rattache pas une fiche sortie du parc) ;
 *   - AUCUNE N'EST « DEJA LIEE AILLEURS » : un champ de lien est la reference de
 *     CETTE fiche, et vingt projets peuvent avoir le meme client sans que le
 *     choix de l'un deplace les autres. La mention n'a pas de sens ici.
 *
 * PAR ORDRE ALPHABETIQUE, casse ignoree, comme la carte.
 */
export function candidatsPourUnChamp(
  records: readonly Record<string, unknown>[],
  /* UNE FICHE (un champ simple) OU TOUTES CELLES D'UNE LISTE (le sens direct
     d'une carte, ou un champ a liens multiples) : les ids deja poses sont
     dehors, tous. */
  valeurEnPlace: string | readonly string[] | null,
  libeller: (record: Record<string, unknown>) => { title: string; subtitle?: string },
): CandidatARattacher[] {
  const enPlace = valeurEnPlace === null ? [] : typeof valeurEnPlace === 'string' ? [valeurEnPlace] : valeurEnPlace;
  const candidats: CandidatARattacher[] = [];
  for (const record of records) {
    const id = record['id'];
    if (typeof id !== 'string' || id === '') continue;
    if (record['archivedAt'] != null) continue;
    if (enPlace.includes(id)) continue;
    const { title, subtitle } = libeller(record);
    candidats.push({
      id,
      label: title,
      dejaLiee: false,
      ...(subtitle === undefined || subtitle === '' ? {} : { sousTitre: subtitle }),
    });
  }
  return candidats.sort((a, b) =>
    a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' }),
  );
}
